// API service for checkout and reward collection
import { logger } from '../utils/logger'
import { PaymentReward, type Extra } from '../types'
import { authenticatedFetch } from './auth'

export interface CheckoutRequest {
    referer: string
    id?: string
    slug?: string
}

export interface CheckoutResponse {
    url: string
    shoppingCartId?: string
    checkoutReceipt?: { price: string; currency: string; primaryType: string | null; primaryAmount: number; shopBonusAmount: number; shopBonusTitle?: string; extras: Omit<Extra, 'meta'>[] }
    shopBonusPercent?: number
    checkoutRewards?: { type: string; amount: number; shopBonusAmount: number; totalAmount: number }[]
}

export const getCheckout = async (request: CheckoutRequest): Promise<CheckoutResponse> => {
    let apiEndpoint: string
    if (request.id) {
        apiEndpoint = '/api/mobile-shop/urlByShoppingCartId'
    } else if (request.slug) {
        apiEndpoint = '/api/mobile-shop/urlByShopItems'
    } else {
        throw new Error('Missing required parameters')
    }
    
    try {
        const response = await authenticatedFetch(apiEndpoint, {
            method: 'POST',
            body: JSON.stringify({ id: request.id, slug: request.slug, referrer: request.referer }),
        })

        const json = await response.json()
        if (!response.ok || json.r === false) throw new Error(json.message || 'Checkout request rejected')
        const data: CheckoutResponse = json.data ?? json
        if (typeof data.url !== 'string' || !data.url) throw new Error('Invalid checkout response')
        return data
    } catch (error) {
        logger.error('API', 'Error fetching checkout URL:', error)
        throw error
    }
}

export const cancelCheckout = async (id: string): Promise<void> => {
    const response = await authenticatedFetch('/api/mobile-shop/cancelCheckout', {
        method: 'POST', body: JSON.stringify({ id }),
    })
    const json = await response.json()
    if (!response.ok || json.r !== true || json.data?.cancelled !== true) {
        throw new Error(json.message || 'Checkout could not be cancelled. Please retry.')
    }
}

export const getCheckoutUrl = async (request: CheckoutRequest): Promise<string> => (await getCheckout(request)).url

export interface CollectRewardsResponse {
    success: boolean
    rewards?: PaymentReward[]
}

export interface CollectRewardResponse {
    success: boolean
    reward?: PaymentReward
}

/**
 * Collects all pending rewards at once
 */
export const collectRewards = async (): Promise<CollectRewardsResponse> => {
    try {
        const response = await authenticatedFetch('/api/mobile-shop/collectRewards', {
            method: 'POST',
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to collect rewards: ${errorText}`)
        }

        const data: CollectRewardsResponse = await response.json()
        return data
    } catch (error) {
        logger.error('API', 'Error collecting rewards:', error)
        throw error
    }
}

/**
 * Collects a single reward by threshold
 */
export const collectReward = async (threshold: number): Promise<CollectRewardResponse> => {
    try {
        const response = await authenticatedFetch('/api/mobile-shop/collectReward', {
            method: 'POST',
            body: JSON.stringify({ threshold }),
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to collect reward: ${errorText}`)
        }

        const data: CollectRewardResponse = await response.json()
        return data
    } catch (error) {
        logger.error('API', 'Error collecting reward:', error)
        throw error
    }
}

